import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommDataGrid from './CommDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  Button,
  DialogContentText,
  IconButton,
  Menu,
  MenuItem,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { removeRankFromUser } from '../api/requests/ranks';
import { useAlert } from '../context/AlertContext';
import CommonDialog from './common/CommonDialog';
import { PaginationType } from '../pages/Groups';
import { subTitleStyles } from './CreateRoleModal';
import { labelStyles } from '../pages/createUser';
import { createGroupByRank, createGroupByRole } from '../api/requests/groups';
import { Role } from '../pages/Roles';

//Images
import AddIcon from '../../src/assets/AddIcon.svg';
import { useAuth } from '../context/AuthContext';
import { Rank } from '../pages/Ranks';
import { containedButtonWithIconSx } from '../pages/Users';

interface UsersByRankIdProps {
  users: any;
  rankName: string;
  section: string;
  fetchUsersByRank: () => void;
  loading: boolean;
  selectedRow: Role | Rank | null;
  onUserDelete?: () => void;
  setPaginationModel?: (model: PaginationType) => void;
  paginationModel?: PaginationType;
  totalRowsCount?: number;
  searchText?: string;
  handleSearch?: (text: string) => void;
}

interface ErrorState {
  group: string;
}

const UsersById = ({
  users,
  rankName,
  fetchUsersByRank,
  onUserDelete,
  setPaginationModel = () => {},
  paginationModel = { page: 0, pageSize: 5 },
  totalRowsCount = 0,
  searchText,
  handleSearch,
  section,
  loading,
  selectedRow,
}: UsersByRankIdProps) => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [usersByRank, setUsersByRank] = useState([]);
  const { showAlert } = useAlert();
  const [deleteDialogId, setDeleteDialogId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [groupName, setGroupName] = useState('');
  const [openCreateGroup, setOpenCreateGroup] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({ group: '' });

  const closeDeleteDialog = () => {
    setDeleteDialogId(null);
    setSelectedUserId(null);
  };

  const openDeleteDialog = (id: number) => {
    setDeleteDialogId(id);
    setSelectedUserId(id);
    handleMenuClose();
  };

  const validateForm = (): boolean => {
    const newErrors: ErrorState = { group: '' };
    let isValid = false;
    if (!groupName.trim()) {
      newErrors.group = 'Group name is required';
      isValid = true;
    }
    setErrors(newErrors);
    return isValid;
  };

  const closeCreateGroupDialog = () => {
    setGroupName('');
    setOpenCreateGroup(false);
  };

  const handleCreateGroupWithMembers = async () => {
    if (validateForm()) {
      console.log('reult', validateForm);
      return;
    }
    if (totalRowsCount === 0) {
      showAlert('No users available to create a group', 'error');
      return;
    }
    try {
      if (!selectedRow) throw new Error('Please select a row first');
      const response = await (section === 'Ranks'
        ? createGroupByRank({
            rankId: selectedRow.id,
            groupName: groupName.trim(),
          })
        : createGroupByRole({
            roleId: selectedRow.id,
            groupName: groupName.trim(),
          }));
      if (response && response.status === 200) {
        showAlert('Group created successfully', 'success');
        closeCreateGroupDialog();
      } else {
        showAlert('Failed to create group', 'error');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      showAlert('Failed to create group', 'error');
    }
  };

  const rankColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      headerClassName: 'custom-header',
      disableColumnMenu: true,
      width: 200,
      renderCell: (params) => params.value,
    },
    {
      field: 'policeStation',
      headerName: 'Police Station',
      flex: 1,
      headerClassName: 'custom-header',
      disableColumnMenu: true,
      renderCell: (params) => params.value,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      headerClassName: 'custom-header',
      disableColumnMenu: true,
      renderCell: (params) => params.value,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      headerClassName: 'custom-header',
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          <IconButton onClick={(event) => handleMenuOpen(event, params.row)}>
            <MoreVertIcon sx={{ color: '#64748B' }} />
          </IconButton>
          {selectedUserId === params.row.id && (
            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              {/* <MenuItem onClick={()=>handleEdit(params.row.id)}>Edit</MenuItem> */}
              <MenuItem onClick={() => openDeleteDialog(params.row.id)}>
                Delete
              </MenuItem>
            </Menu>
          )}
        </>
      ),
    },
  ];

  const roleColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Full Name',
      flex: 1.5,
      headerClassName: 'custom-header',
      disableColumnMenu: true,
      renderCell: (params) => params.value,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      headerClassName: 'custom-header',
      disableColumnMenu: true,
      renderCell: (params) => params.value,
    },
  ];

  const handleDelete = async () => {
    try {
      const response =
        deleteDialogId && (await removeRankFromUser(deleteDialogId));
      if (response && response.status === 200) {
        closeDeleteDialog();
        fetchUsersByRank();
        if (onUserDelete) onUserDelete();
        showAlert('User deleted successfully', 'success');
      }
    } catch {
      showAlert('An error occurred', 'error');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedUserId(row.id);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  useEffect(() => {
    if (section === 'Ranks') {
      setUsersByRank(
        users?.map((user: any) => ({
          id: user.userId,
          name: user.fullName,
          policeStation: user.policeStation,
          location: user.location,
        }))
      );
    } else {
      setUsersByRank(
        users?.map((user: any) => ({
          id: user.userId,
          name: user.fullname,
          email: user.email,
        }))
      );
    }
  }, [users]);

  const openCreateGroupDialog = useCallback(() => {
    setOpenCreateGroup(true);
  }, []);

  const buttonsComponent = useMemo(
    () => (
      <>
        {user?.permissions?.some(
          (p) => p.module === 'Group' && p.actions.includes('Create')
        ) && (
          <Button
            variant='contained'
            onClick={openCreateGroupDialog}
            sx={containedButtonWithIconSx}
          >
            <img src={AddIcon} /> Create Group{' '}
          </Button>
        )}
      </>
    ),
    [user, openCreateGroup]
  );

  return (
    <Box
      sx={{
        height: 'auto',
      }}
    >
      <CommDataGrid
        pageSize={7}
        columns={section === 'Ranks' ? rankColumns : roleColumns}
        rows={usersByRank}
        title=''
        description={`Users with ${rankName || ''}`}
        section='Users'
        setPaginationModel={setPaginationModel}
        renderButtons={() => buttonsComponent}
        paginationModel={paginationModel}
        totalRowsCount={totalRowsCount}
        searchText={searchText}
        handleSearch={handleSearch}
        loading={loading}
      />
      {openCreateGroup && (
        <CommonDialog
          title='Create Group'
          cancelButton={closeCreateGroupDialog}
          actionButton={handleCreateGroupWithMembers}
          actionButtonTitle='Create group'
          cancelButtonTitle='Cancel'
        >
          <DialogContentText>
            <Box sx={dialogContextChildWrapperSx}>
              <Typography sx={subTitleStyles}>
                Group Name <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter group name'
                value={groupName}
                onChange={(e) => {
                  setGroupName(e.target.value);
                  if (errors.group) {
                    setErrors((prev) => ({ ...prev, group: '' }));
                  }
                }}
                error={!!errors.group}
                helperText={errors.group}
                slotProps={{
                  input: {
                    sx: labelStyles,
                  },
                }}
              />
            </Box>
          </DialogContentText>
        </CommonDialog>
      )}
      {!!deleteDialogId && (
        <CommonDialog
          title='Delete User'
          cancelButton={closeDeleteDialog}
          actionButton={handleDelete}
          actionButtonTitle='Yes'
          cancelButtonTitle='cancel'
        >
          <DialogContentText>
            <Typography
              fontSize='13px'
              sx={{ color: '#000000', paddingBottom: 1 }}
            >
              Are you sure you want to delete the user?
            </Typography>
            <Typography fontSize='12px'>
              {`The user will be deleted if you click on 'Yes'.`}
            </Typography>
          </DialogContentText>
        </CommonDialog>
      )}
    </Box>
  );
};

export default UsersById;

export const dialogContextChildWrapperSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  pl: 2,
  pr: 2,
  width: 500,
};
